/* @flow */
import * as React from "react";
import { connect } from "react-redux";
import { inferLocale, getDirection } from "./common/Gettext";
import withData from "./withData";

import type { Dispatch } from "../redux/action";

type Currency = {
  symbol: string,
  rawSymbol: string,
  pattern: Array<"sign" | "space" | "symbol" | "value">,
  internationalSymbol: {
    [locale: string]: string,
    default: string
  }
};

export function mapStateToCurrency(
  state: $FlowFixMeState,
  ownProps: $FlowFixMeProps
) {
  if (state.apiConfigs == null || state.serverData == null) {
    return { empty: true };
  }

  const { apiConfigs, serverData } = state;
  const locale = inferLocale(state);

  // irontrade config copy for siam (for now)
  const fallbackUSD = {
    symbol: "&#36;",
    rawSymbol: "$",
    internationalSymbol: {
      en: "$",
      abbr: "USD",
      default: "$"
    },
    // bids: [5, 10, 20, 30, 50, 100, 150, 200],
    // initialDeposit: 1000,
    // minFillup: 10,
    // minWithdraw: 20,
    // vipPayment: 900,
    // minBidAmount: 1,
    pattern: ["symbol", "sign", "value"]
  };

  if (typeof ownProps === "object" && ownProps.currency != null) {
    if (
      ownProps.currency === "USD" &&
      !apiConfigs.currencySettings[ownProps.currency]
    ) {
      return {
        currency: fallbackUSD,
        locale: locale
      };
    }

    if (typeof ownProps.currency === "object") {
      return {
        currency: ownProps.currency,
        locale: locale
      };
    }

    return {
      currency: apiConfigs.currencySettings[ownProps.currency],
      locale: locale
    };
  } else {
    return {
      currency:
        apiConfigs.currencySettings[
          serverData.currency || apiConfigs.defaultCurrency
        ],
      locale: locale
    };
  }
}

function withCurrency(Component) {
  function EmptyWhileNoData({ empty, ...props }) {
    return empty ? null : <Component {...props} />;
  }

  return connect(mapStateToCurrency)(EmptyWhileNoData);
}

// const withCurrency = withData({
//   required: ["apiConfigs", "serverData"],
//   spinner: "simple",
//   selector: mapStateToCurrency
// });

export const CurrencySymbol = withCurrency(
  ({ currency, locale, dispatch, ...rest }) => (
    <span {...rest}>{getSymbol(currency, locale)}</span>
  )
);

const NBSP = "\u00a0";

type Options = {
  digits?: number
};

function formatMoney(
  amount: number,
  options: Options
): { sign: string, value: string } {
  if (options.digits === "number") {
    return {
      value: Math.abs(amount).toFixed(options.digits),
      sign: amount < 0 ? "-" : ""
    };
  } else {
    const withFraction = Math.abs(amount).toFixed(2);
    const withoutFraction = Math.abs(amount).toFixed(0);

    return {
      value:
        withFraction === withoutFraction + ".00"
          ? withoutFraction
          : withFraction,
      sign: amount < 0 ? "-" : ""
    };
  }
}

function getSymbol(currency: Currency, locale: string): string {
  return (
    currency && (currency.internationalSymbol[locale] || currency.internationalSymbol.default)
  );
}

type MoneyProps = {
  rawSymbol: string,
  children: string | number | Array<*>,
  currency: Object,
  locale: string,
  dispatch: Dispatch,
  Value: React.ComponentType<*>,
  Symbol: React.ComponentType<*>,
  Sign: React.ComponentType<*>,
  digits?: number,
  className?: string
};

function Money({
  children,
  currency,
  locale,
  digits,
  dispatch,
  Value = "span",
  Symbol = "span",
  Sign = "span",
  isTradePage,
  ...props
}: MoneyProps) {
  children = children || 0;

  const units = {
    ...formatMoney(parseFloat(children), { digits }),
    space: NBSP,
    symbol: getSymbol(currency, locale)
  };

  const output = currency.pattern.map((key, i) => {
    const unit = units[key];

    switch (key) {
      case "sign":
        return <Sign key={key + i}>{unit}</Sign>;
      case "space":
        return unit;
      case "symbol":
        return (
          <Symbol
            key={key + i}
            style={{ whiteSpace: "nowrap" }}
            istradepage={isTradePage}
          >
            {unit}
          </Symbol>
        );
      case "value":
        return (
          <Value key={key + i} istradepage={isTradePage}>
            {unit}
          </Value>
        );
      default:
        return null;
    }
  });

  return (
    <span
      className={props.className}
      style={{
        direction: getDirection(locale),
        display: "inline-block"
      }}
    >
      {output}
    </span>
  );
}

// withCurrency не пробрасывает статические методы,
// и у меня нет времени её этому учить, поэтому мы
// приклеиваем  методы к эксортируемому компоненту.

const _Money = withCurrency(Money);

_Money.Value = "span";
_Money.Symbol = "span";
_Money.Sign = "span";

export default _Money;

// withMoney() это HoC, снабжающий компонент
// реквизитом `money()`, который форматирует
// деньги в строку.

export const withMoney = withData({
  required: ["apiConfigs", "serverData"],
  spinner: "simple",
  selector(state) {
    return {
      money(amount: number, options: Options = {}): string {
        const { currency } = mapStateToCurrency(state, options);

        const units = {
          ...formatMoney(amount, options),
          space: NBSP,
          symbol: currency.rawSymbol
        };

        return currency.pattern.map(key => units[key]).join("");
      }
    };
  }
});



// WEBPACK FOOTER //
// ./src/components/Money.js